import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  DiversityColors, DiversityDemographicData,
  DiversityDemographicDataByStage,
  DiversityOptions,
  FunnelSelectionDetails
} from '../../../../model';
import {DiversityMetricsSegmented, DiversityMetricsItem} from '../../client-api.service';
import {TranslateService} from '@ngx-translate/core';
import {DiversityInsightService} from '../service/diversity-insight.service';


export interface ChartData {
  year: number;
  quarter: string;
  hidden: boolean;
  orderCount: number;
  data: DiversityDemographicData[];
  entireNotEnoughData: boolean;
}

@Component({
  selector: 'horizontal-diversity-bar-chart',
  templateUrl: './horizontal-diversity-bar-chart.component.html',
  styleUrls: ['./horizontal-diversity-bar-chart.component.scss']
})
export class HorizontalDiversityBarChartComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() selectedTab: string;
  @Input() metrics: DiversityMetricsSegmented[];
  @Output() funnelSelectionDetails = new EventEmitter<FunnelSelectionDetails>();
  showNotEnoughDataScreen: boolean;

  formattedSubmittedStageCount: number;
  formattedInterviewedStageCount: number;
  formattedHiredStageCount: number;
  Translate: TranslateService;

  SUBMITTED: string;
  INTERVIEWED: string;
  HIRED: string;
  UNKNOWN: string;

  SUBMITTED_DATA_INDEX = 0;
  INTERVIEWED_DATA_INDEX = 1;
  HIRED_DATA_INDEX = 2;

  TALENT_DATA_COUNT_THRESHOLD = 3;

  public data = [
    {
      year: -1,
      quarter: '',
      hidden: false,
      orderCount: 0,
      data: [{
        label: this.SUBMITTED,
        data: []
        },
        {
          label: this.INTERVIEWED,
          data: []
        },
        {
          label: this.HIRED,
          data: []
        }],
      entireNotEnoughData: false
    }
  ];
  public colors = [DiversityColors.gray,
    DiversityColors.red,
    DiversityColors.orange,
    DiversityColors.blue,
    DiversityColors.green,
    DiversityColors.orange];

  constructor(Translate: TranslateService,
              private diversityInsightService: DiversityInsightService,
              private cdr: ChangeDetectorRef) {
    this.Translate = Translate;
    this.SUBMITTED = this.Translate.instant('diversity.submitted');
    this.INTERVIEWED = this.Translate.instant('diversity.interviewed');
    this.HIRED = this.Translate.instant('diversity.hired');
    this.UNKNOWN = this.Translate.instant('diversity.unknown');
  }

  ngOnInit(): void {
    this.updateChartData();
    this.diversityInsightService.showNotEnoughInfoScreen$.subscribe(
      showNotEnoughInfoScreen => this.showNotEnoughDataScreen = showNotEnoughInfoScreen
    );
    this.diversityInsightService.selectedYearsAndQuartersUpdated$.subscribe(_ => {
      this.updateChartData();
    });
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateChartData();
  }

  emitFunnelSelectionDetails(stage, year, quarter) {
    this.funnelSelectionDetails.emit({
      selectedFunnelStage: stage,
      selectedYear: year,
      selectedQuarter: quarter
    });
  }

  private updateChartData() {
    this.data = [];
    for (const metric of this.metrics) {
      if (this.diversityInsightService.dateSelection.years.includes(metric.year.toString()) &&
          this.diversityInsightService.dateSelection.quarters.includes(this.Translate.instant(`diversity.quarters.q${metric.quarter}`))) {
        const chartData = this.getChartData(metric);
        this.data.push(chartData);
      }
    }
    this.diversityInsightService.selectedDiversityTab = {name: this.selectedTab, data: this.data};
    const numOfNotEnoughDataQuarters = this.data.filter((row) => row.entireNotEnoughData).length;
    this.showNotEnoughDataScreen = numOfNotEnoughDataQuarters === this.data.length;
  }



  getChartData(metric: DiversityMetricsSegmented): ChartData {
    const currentDataSet: ChartData = {
      year: metric.year,
      quarter: this.Translate.instant('diversity.quarters.q' + metric.quarter.toString()),
      hidden: false,
      orderCount: 0,
      data: [
        {
          label: this.SUBMITTED,
          data: []
        },
        {
          label: this.INTERVIEWED,
          data: []
        },
        {
          label: this.HIRED,
          data: []
        }
      ],
      entireNotEnoughData: false
    };
    this.formattedSubmittedStageCount = metric.submitted.stageCount === 0 ? 1 : metric.submitted.stageCount;
    this.formattedInterviewedStageCount = metric.interviewed.stageCount === 0 ? 1 : metric.interviewed.stageCount;
    this.formattedHiredStageCount = metric.hired.stageCount === 0 ? 1 : metric.hired.stageCount;

    currentDataSet.orderCount = metric.quarterOrderCount;
    currentDataSet.data[this.SUBMITTED_DATA_INDEX].label = this.SUBMITTED + ` (${this.formattedSubmittedStageCount})`;
    currentDataSet.data[this.INTERVIEWED_DATA_INDEX].label = this.INTERVIEWED + ` (${this.formattedInterviewedStageCount})`;
    currentDataSet.data[this.HIRED_DATA_INDEX].label = this.HIRED + ` (${this.formattedHiredStageCount})`;

    let submittedNotEnoughData = false;
    let interviewedNotEnoughData = false;
    let hiredNotEnoughData = false;

    switch (this.selectedTab) {
      case DiversityOptions.gender:
        const genderData = this.getGenderData(metric);
        currentDataSet.data[this.SUBMITTED_DATA_INDEX].data = genderData.submitted;
        currentDataSet.data[this.INTERVIEWED_DATA_INDEX].data = genderData.interviewed;
        currentDataSet.data[this.HIRED_DATA_INDEX].data = genderData.hired;
        submittedNotEnoughData = genderData.submitted.filter((data) => data.notEnoughData).length === 1;
        interviewedNotEnoughData = genderData.interviewed.filter((data) => data.notEnoughData).length === 1;
        hiredNotEnoughData = genderData.hired.filter((data) => data.notEnoughData).length === 1;
        if (submittedNotEnoughData &&  interviewedNotEnoughData && hiredNotEnoughData) {
          currentDataSet.entireNotEnoughData = true;
        }
        break;
      case DiversityOptions.ethnicity:
        const ethnicityData = this.getEthnicityData(metric);
        currentDataSet.data[this.SUBMITTED_DATA_INDEX].data = ethnicityData.submitted;
        currentDataSet.data[this.INTERVIEWED_DATA_INDEX].data = ethnicityData.interviewed;
        currentDataSet.data[this.HIRED_DATA_INDEX].data = ethnicityData.hired;
        submittedNotEnoughData = ethnicityData.submitted.filter((data) => data.notEnoughData).length === 1;
        interviewedNotEnoughData = ethnicityData.interviewed.filter((data) => data.notEnoughData).length === 1;
        hiredNotEnoughData = ethnicityData.hired.filter((data) => data.notEnoughData).length === 1;
        if (submittedNotEnoughData &&  interviewedNotEnoughData && hiredNotEnoughData) {
          currentDataSet.entireNotEnoughData = true;
        }
        break;
      case DiversityOptions.neurodiversity:
        const neurodiversityData = this.getNeurodiversityData(metric);
        currentDataSet.data[this.SUBMITTED_DATA_INDEX].data = neurodiversityData.submitted;
        currentDataSet.data[this.INTERVIEWED_DATA_INDEX].data = neurodiversityData.interviewed;
        currentDataSet.data[this.HIRED_DATA_INDEX].data = neurodiversityData.hired;
        submittedNotEnoughData = neurodiversityData.submitted.filter((data) => data.notEnoughData).length === 1;
        interviewedNotEnoughData = neurodiversityData.interviewed.filter((data) => data.notEnoughData).length === 1;
        hiredNotEnoughData = neurodiversityData.hired.filter((data) => data.notEnoughData).length === 1;
        if (submittedNotEnoughData &&  interviewedNotEnoughData && hiredNotEnoughData) {
          currentDataSet.entireNotEnoughData = true;
        }
        break;
      case DiversityOptions.disability:
        const disabilityData = this.getDisabilityData(metric);
        currentDataSet.data[this.SUBMITTED_DATA_INDEX].data = disabilityData.submitted;
        currentDataSet.data[this.INTERVIEWED_DATA_INDEX].data = disabilityData.interviewed;
        currentDataSet.data[this.HIRED_DATA_INDEX].data = disabilityData.hired;
        submittedNotEnoughData = disabilityData.submitted.filter((data) => data.notEnoughData).length === 1;
        interviewedNotEnoughData = disabilityData.interviewed.filter((data) => data.notEnoughData).length === 1;
        hiredNotEnoughData = disabilityData.hired.filter((data) => data.notEnoughData).length === 1;
        if (submittedNotEnoughData &&  interviewedNotEnoughData && hiredNotEnoughData) {
          currentDataSet.entireNotEnoughData = true;
        }
        break;
      case DiversityOptions.veteran:
        const veteranData = this.getVeteranData(metric);
        currentDataSet.data[this.SUBMITTED_DATA_INDEX].data = veteranData.submitted;
        currentDataSet.data[this.INTERVIEWED_DATA_INDEX].data = veteranData.interviewed;
        currentDataSet.data[this.HIRED_DATA_INDEX].data = veteranData.hired;
        submittedNotEnoughData = veteranData.submitted.filter((data) => data.notEnoughData).length === 1;
        interviewedNotEnoughData = veteranData.interviewed.filter((data) => data.notEnoughData).length === 1;
        hiredNotEnoughData = veteranData.hired.filter((data) => data.notEnoughData).length === 1;
        if (submittedNotEnoughData &&  interviewedNotEnoughData && hiredNotEnoughData) {
          currentDataSet.entireNotEnoughData = true;
        }
        break;
      case DiversityOptions.lgbtqia:
        const lgbtqiaData = this.getLgbtqiaData(metric);
        currentDataSet.data[this.SUBMITTED_DATA_INDEX].data = lgbtqiaData.submitted;
        currentDataSet.data[this.INTERVIEWED_DATA_INDEX].data = lgbtqiaData.interviewed;
        currentDataSet.data[this.HIRED_DATA_INDEX].data = lgbtqiaData.hired;
        submittedNotEnoughData = lgbtqiaData.submitted.filter((data) => data.notEnoughData).length === 1;
        interviewedNotEnoughData = lgbtqiaData.interviewed.filter((data) => data.notEnoughData).length === 1;
        hiredNotEnoughData = lgbtqiaData.hired.filter((data) => data.notEnoughData).length === 1;
        if (submittedNotEnoughData &&  interviewedNotEnoughData && hiredNotEnoughData) {
          currentDataSet.entireNotEnoughData = true;
        }
        break;
    }

    return currentDataSet;

  }

  buildDataSet(metrics: DiversityMetricsItem,
               data: number[],
               category: string,
               dataFunction: (data: number[], category: string) => DiversityDemographicDataByStage[]): DiversityDemographicDataByStage[] {
    if (metrics.talentCount < this.TALENT_DATA_COUNT_THRESHOLD) {
      return [{
        label: this.Translate.instant('diversity.not-enough-data'),
        percentage: 100,
        count: 0,
        color: DiversityColors.notAsLightGray,
        notEnoughData: true
      }];
    }

    return dataFunction(data, category);
  }

  getGenderData(metrics) {
    const buildGenderDataSet = (genderData, category): DiversityDemographicDataByStage[] => [
      {
        label: this.Translate.instant('diversity.gender.female'),
        percentage: this.calculatePercentage(genderData[3], category),
        count: genderData[3],
        color: DiversityColors.orange
      },
      {
        label: this.Translate.instant('diversity.gender.male'),
        percentage: this.calculatePercentage(genderData[2], category),
        count: genderData[2],
        color: DiversityColors.blue
      },
      {
        label: this.Translate.instant('diversity.gender.non-binary'),
        percentage: this.calculatePercentage(genderData[1], category),
        count: genderData[1],
        color: DiversityColors.green
      },
      {
        label: this.UNKNOWN,
        percentage: this.calculatePercentage(genderData[0], category),
        count: genderData[0],
        color: DiversityColors.gray
      }
    ];

    return {
      submitted: this.buildDataSet(metrics.submitted, [
        metrics.submitted.genderUnknown + metrics.submitted.genderNotDisclosed,
        metrics.submitted.genderNonBinary,
        metrics.submitted.genderMale,
        metrics.submitted.genderFemale
      ], this.SUBMITTED, buildGenderDataSet),
      interviewed: this.buildDataSet(metrics.interviewed, [
        metrics.interviewed.genderUnknown + metrics.interviewed.genderNotDisclosed,
        metrics.interviewed.genderNonBinary,
        metrics.interviewed.genderMale,
        metrics.interviewed.genderFemale
      ], this.INTERVIEWED, buildGenderDataSet),
      hired: this.buildDataSet(metrics.hired, [
        metrics.hired.genderUnknown + metrics.hired.genderNotDisclosed,
        metrics.hired.genderNonBinary,
        metrics.hired.genderMale,
        metrics.hired.genderFemale
      ], this.HIRED, buildGenderDataSet),
      entireNotEnoughData: false
    };
  }

  getEthnicityData(metrics) {
    const buildEthnicityDataSet = (ethnicityData, category): DiversityDemographicDataByStage[] => [
      {
        label: this.Translate.instant('diversity.ethnicity.white'),
        percentage: this.calculatePercentage(ethnicityData[7], category),
        count: ethnicityData[7],
        color: DiversityColors.orange
      },
      {
        label: this.Translate.instant('diversity.ethnicity.black-african-american'),
        percentage: this.calculatePercentage(ethnicityData[6], category),
        count: ethnicityData[6],
        color: DiversityColors.blue
      },
      {
        label: this.Translate.instant('diversity.ethnicity.hispanic-latino'),
        percentage: this.calculatePercentage(ethnicityData[5], category),
        count: ethnicityData[5],
        color: DiversityColors.green
      },
      {
        label: this.Translate.instant('diversity.ethnicity.asian'),
        percentage: this.calculatePercentage(ethnicityData[4], category),
        count: ethnicityData[4],
        color: DiversityColors.purple
      },
      {
        label: this.Translate.instant('diversity.ethnicity.american-indian'),
        percentage: this.calculatePercentage(ethnicityData[3], category),
        count: ethnicityData[3],
        color: DiversityColors.brown
      },
      {
        label: this.Translate.instant('diversity.ethnicity.native-hawaiian'),
        percentage: this.calculatePercentage(ethnicityData[2], category),
        count: ethnicityData[2],
        color: DiversityColors.blueGray
      },
      {
        label: this.Translate.instant('diversity.ethnicity.two-races'),
        percentage: this.calculatePercentage(ethnicityData[1], category),
        count: ethnicityData[1],
        color: DiversityColors.red
      },
      {
        label: this.UNKNOWN,
        percentage: this.calculatePercentage(ethnicityData[0], category),
        count: ethnicityData[0],
        color: DiversityColors.gray
      },
    ];

    return {
      submitted: this.buildDataSet(metrics.submitted, [
        metrics.submitted.ethnicityUnknown + metrics.submitted.ethnicityNotDisclosed,
        metrics.submitted.ethnicityTwoOrMoreRaces,
        metrics.submitted.ethnicityNativeHawaiianOrOtherPacificIslander,
        metrics.submitted.ethnicityAmericanIndianOrAlaskanNative,
        metrics.submitted.ethnicityAsian,
        metrics.submitted.ethnicityHispanicOrLatino,
        metrics.submitted.ethnicityBlackOrAfricanAmerican,
        metrics.submitted.ethnicityWhite
      ], this.SUBMITTED, buildEthnicityDataSet),
      interviewed: this.buildDataSet(metrics.interviewed, [
        metrics.interviewed.ethnicityUnknown + metrics.interviewed.ethnicityNotDisclosed,
        metrics.interviewed.ethnicityTwoOrMoreRaces,
        metrics.interviewed.ethnicityNativeHawaiianOrOtherPacificIslander,
        metrics.interviewed.ethnicityAmericanIndianOrAlaskanNative,
        metrics.interviewed.ethnicityAsian,
        metrics.interviewed.ethnicityHispanicOrLatino,
        metrics.interviewed.ethnicityBlackOrAfricanAmerican,
        metrics.interviewed.ethnicityWhite
      ], this.INTERVIEWED, buildEthnicityDataSet),
      hired: this.buildDataSet(metrics.hired, [
        metrics.hired.ethnicityUnknown + metrics.hired.ethnicityNotDisclosed,
        metrics.hired.ethnicityTwoOrMoreRaces,
        metrics.hired.ethnicityNativeHawaiianOrOtherPacificIslander,
        metrics.hired.ethnicityAmericanIndianOrAlaskanNative,
        metrics.hired.ethnicityAsian,
        metrics.hired.ethnicityHispanicOrLatino,
        metrics.hired.ethnicityBlackOrAfricanAmerican,
        metrics.hired.ethnicityWhite
      ], this.HIRED, buildEthnicityDataSet),
      entireNotEnoughData: false
    };
  }

  getNeurodiversityData(metrics) {
    const buildNeurodiversityDataSet = (neurodiversityData, category): DiversityDemographicDataByStage[] => [
      {
        label: this.Translate.instant('diversity.neurodiversity.neurodivergent'),
        percentage: this.calculatePercentage(neurodiversityData[2], category),
        count: neurodiversityData[2],
        color: DiversityColors.orange
      },
      {
        label: this.Translate.instant('diversity.neurodiversity.neurotypical'),
        percentage: this.calculatePercentage(neurodiversityData[1], category),
        count: neurodiversityData[1],
        color: DiversityColors.blue
      },
      {
        label: this.UNKNOWN,
        percentage: this.calculatePercentage(neurodiversityData[0], category),
        count: neurodiversityData[0],
        color: DiversityColors.gray
      }
    ];

    return {
      submitted: this.buildDataSet(metrics.submitted, [
        metrics.submitted.neurodivergentUnknown + metrics.submitted.neurodivergentNotDisclosed,
        metrics.submitted.neurodivergentNo,
        metrics.submitted.neurodivergentYes
      ], this.SUBMITTED, buildNeurodiversityDataSet),
      interviewed: this.buildDataSet(metrics.interviewed, [
        metrics.interviewed.neurodivergentUnknown + metrics.interviewed.neurodivergentNotDisclosed,
        metrics.interviewed.neurodivergentNo,
        metrics.interviewed.neurodivergentYes
      ], this.INTERVIEWED, buildNeurodiversityDataSet),
      hired: this.buildDataSet(metrics.hired, [
        metrics.hired.neurodivergentUnknown + metrics.interviewed.neurodivergentNotDisclosed,
        metrics.hired.neurodivergentNo,
        metrics.hired.neurodivergentYes
      ], this.HIRED, buildNeurodiversityDataSet),
      entireNotEnoughData: false
    };
  }

  getDisabilityData(metrics) {
    const buildDisabilityDataSet = (disabilityData, category): DiversityDemographicDataByStage[] => [
      {
        label: this.Translate.instant('diversity.disability.disabled'),
        percentage: this.calculatePercentage(disabilityData[2], category),
        count: disabilityData[2],
        color: DiversityColors.orange
      },
      {
        label: this.Translate.instant('diversity.disability.not-disabled'),
        percentage: this.calculatePercentage(disabilityData[1], category),
        count: disabilityData[1],
        color: DiversityColors.blue
      },
      {
        label: this.UNKNOWN,
        percentage: this.calculatePercentage(disabilityData[0], category),
        count: disabilityData[0],
        color: DiversityColors.gray
      }
    ];

    return {
      submitted: this.buildDataSet(metrics.submitted, [
        metrics.submitted.disabledUnknown + metrics.submitted.disabledNotDisclosed,
        metrics.submitted.disabledNo,
        metrics.submitted.disabledYes
      ], this.SUBMITTED, buildDisabilityDataSet),
      interviewed: this.buildDataSet(metrics.interviewed, [
        metrics.interviewed.disabledUnknown + metrics.interviewed.disabledNotDisclosed,
        metrics.interviewed.disabledNo,
        metrics.interviewed.disabledYes
      ], this.INTERVIEWED, buildDisabilityDataSet),
      hired: this.buildDataSet(metrics.hired, [
        metrics.hired.disabledUnknown + metrics.hired.disabledNotDisclosed,
        metrics.hired.disabledNo,
        metrics.hired.disabledYes
      ], this.HIRED, buildDisabilityDataSet),
      entireNotEnoughData: false
    };
  }

  getVeteranData(metrics) {

    const buildVeteranDataSet = (veteranData, category): DiversityDemographicDataByStage[] => [
      {
        label: this.Translate.instant('diversity.veteran.veteran'),
        percentage: this.calculatePercentage(veteranData[2], category),
        count: veteranData[2],
        color: DiversityColors.orange
      },
      {
        label: this.Translate.instant('diversity.veteran.not-a-veteran'),
        percentage: this.calculatePercentage(veteranData[1], category),
        count: veteranData[1],
        color: DiversityColors.blue
      },
      {
        label: this.UNKNOWN,
        percentage: this.calculatePercentage(veteranData[0], category),
        count: veteranData[0],
        color: DiversityColors.gray
      }
    ];

    return {
      submitted: this.buildDataSet(metrics.submitted, [
        metrics.submitted.veteranNotDisclosed,
        metrics.submitted.veteranNotAVeteran,
        metrics.submitted.veteranIsVeteran
      ], this.SUBMITTED, buildVeteranDataSet),
      interviewed: this.buildDataSet(metrics.interviewed, [
        metrics.interviewed.veteranNotDisclosed,
        metrics.interviewed.veteranNotAVeteran,
        metrics.interviewed.veteranIsVeteran
      ], this.INTERVIEWED, buildVeteranDataSet),
      hired: this.buildDataSet(metrics.hired, [
        metrics.hired.veteranNotDisclosed,
        metrics.hired.veteranNotAVeteran,
        metrics.hired.veteranIsVeteran
      ], this.HIRED, buildVeteranDataSet),
      entireNotEnoughData: false
    };

  }

  getLgbtqiaData(metrics) {

    const buildLgbtqiaDataSet = (lgbtqiaData, category): DiversityDemographicDataByStage[] => [
      {
        label: this.Translate.instant('diversity.lgbtqia.member'),
        percentage: this.calculatePercentage(lgbtqiaData[2], category),
        count: lgbtqiaData[2],
        color: DiversityColors.orange
      },
      {
        label: this.Translate.instant('diversity.lgbtqia.not-a-member'),
        percentage: this.calculatePercentage(lgbtqiaData[1], category),
        count: lgbtqiaData[1],
        color: DiversityColors.blue
      },
      {
        label: this.UNKNOWN,
        percentage: this.calculatePercentage(lgbtqiaData[0], category),
        count: lgbtqiaData[0],
        color: DiversityColors.gray
      }
    ];

    return {
      submitted: this.buildDataSet(metrics.submitted, [
        metrics.submitted.lgbtqUnknown + metrics.submitted.lgbtqNotDisclosed,
        metrics.submitted.lgbtqNonMember,
        metrics.submitted.lgbtqMember,
      ], this.SUBMITTED, buildLgbtqiaDataSet),
      interviewed: this.buildDataSet(metrics.interviewed, [
        metrics.interviewed.lgbtqUnknown + metrics.interviewed.lgbtqNotDisclosed,
        metrics.interviewed.lgbtqNonMember,
        metrics.interviewed.lgbtqMember,
      ], this.INTERVIEWED, buildLgbtqiaDataSet),
      hired: this.buildDataSet(metrics.hired, [
        metrics.hired.lgbtqUnknown + metrics.hired.lgbtqNotDisclosed,
        metrics.hired.lgbtqNonMember,
        metrics.hired.lgbtqMember,
      ], this.HIRED, buildLgbtqiaDataSet),
      entireNotEnoughData: false
    };
  }

  private calculatePercentage(dataPoint, stage) {
    switch (stage) {
      case this.SUBMITTED:
        return dataPoint / this.formattedSubmittedStageCount * 100;
      case this.INTERVIEWED:
        return dataPoint / this.formattedInterviewedStageCount * 100;
      case this.HIRED:
        return dataPoint / this.formattedHiredStageCount * 100;
      default:
        console.error('Invalid stage submitted to function calculatePercentage');
    }
  }

}
